import { api, API_BASE_URL } from "./api";

import type { LoginResult, AuthResponse, UserProfile } from "@/types/auth";

/**
 * Trimmed copy of guest's AuthService: Eriol Go is browser-PWA only, so the
 * whole Electron accessKey/deep-link machinery is gone. Auth = HttpOnly
 * cookie set by the backend on login + CSRF double-submit (handled in api.ts).
 */
class AuthService {
  async login(
    email: string,

    password: string,

    totpCode?: string,
  ): Promise<LoginResult> {
    return api.post<LoginResult>("/auth/login", {
      email,

      password,

      ...(totpCode ? { totpCode } : {}),
    });
  }

  async forgotPassword(
    email: string,
  ): Promise<{ success: boolean; message: string }> {
    return api.post("/auth/forgot-password", { email });
  }

  async logout(): Promise<void> {
    await api.authPost("/auth/logout", {});
  }

  getGoogleAuthUrl(): string {
    // The PWA stays in the same window so the HTTPS round-trip back to
    // /auth-callback lands inside the PWA. `from=mobile-pwa` tells the
    // backend which redirect URL to use (same pattern as guest-pwa).
    return `${API_BASE_URL}/auth/google?from=mobile-pwa`;
  }

  async verifyGoogleTwoFactor(
    pendingToken: string,

    totpCode: string,
  ): Promise<AuthResponse> {
    return api.post<AuthResponse>("/auth/2fa/verify-google-login", {
      pendingToken,

      totpCode,
    });
  }

  async getMe(): Promise<UserProfile> {
    const result = await api.authGet<{ data: UserProfile }>("/auth/me");

    return result.data;
  }

  async validateToken(): Promise<boolean> {
    try {
      await this.getMe();

      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : "";

      if (message.includes("401") || message.includes("Unauthorized")) {
        return false;
      }

      // Network error / backend down — assume session is still valid

      return true;
    }
  }
}

export const authService = new AuthService();

export default authService;
